<script setup lang="ts">
import {f7} from "framework7-vue";
import {FirebaseService} from "@/services/firebase/FirebaseService";
import RouterService from "@/services/RouterService";
import AppController from "@/targets/integration/components/App/ts/AppController";
import NewPaymentController, { CreatePaymentFlow } from "../payment-create-config/NewPaymentController";
import UtilsService from "@/services/others/UtilsService";
import {computed} from "vue";
import {FirebaseEvents} from "@/services/firebase/analytics/FirebaseEvents";
import FirebaseEventBuilder from "@/services/firebase/analytics/FirebaseEventBuilder";
import {useTranslate} from "@tolgee/vue";
import {UserError} from "@enums/ServerErrorCodes";

const viewController = NewPaymentController.getInstance();

const {
	account,
	amount,
	flow,
	isSubmitLoading,
} = viewController;

const utilsService = UtilsService.of();

const currencyFormatterAccount = computed(() => utilsService.currencyFormatter(account.value.currency.name));

const { t } = useTranslate();

const handleBack = async () => {
	f7.views.main.router.back('/payments');
	await openSheetModalForNewPayment();
}

const routerService: RouterService = AppController.getInstance().routerService;

const openSheetModalForNewPayment = async () => {
	try {
		await routerService.toCreatePaymentPopup();
        flow.value = CreatePaymentFlow.fromBromoney;
    } catch (e) {
        flow.value = CreatePaymentFlow.classic;
		f7.dialog.alert(t.value("g.payments.alert.first"), t.value("g.payments.alert.second"));
	}
}

const trackAnalytics = (event: FirebaseEvents) => {
	FirebaseService.of().analyticsEvent(
		event,
		FirebaseEventBuilder.of().build()
	);
};

const createPayment = async () => {
	trackAnalytics(FirebaseEvents.PAYMENT_CREATING_TRY);
	viewController.paymentService.payment.value = await viewController.next();
	trackAnalytics(FirebaseEvents.PAYMENT_CREATING_SUCCESS);
}

const handleSubmit = async () => {
	try {
		isSubmitLoading.value = true;
        await createPayment();
        f7.views.main.router.navigate('/payment');
	} catch (e: any) {
			switch (e.code) {
				case UserError.UserTemporarilyBlocked:
					await routerService.toPayments();
					const el: HTMLDivElement | null = document.querySelector('.no-agents-sheet');

					if (el) {
						const sheet = f7.sheet.create({ el });
						sheet.open();
					}
					break;
				default:
					f7.dialog.alert(t.value("g.payments.alert.first"), t.value("g.payments.alert.second"));
			}
    } finally {
        isSubmitLoading.value = false;
    }
}
</script>

<template>
    <f7-page
        class="payment-confirmation-wallet"
    >
        <f7-navbar class="payment-confirmation-wallet__navbar">
            <f7-nav-left>
                <a href="#" class="link icon-only" @click="handleBack">
                    <i class="icon-back icon"></i>
                </a>
            </f7-nav-left>
            <f7-nav-title>
                {{ $t('payment.confirm-wallet.navbar-title') }}
            </f7-nav-title>
        </f7-navbar>

        <f7-page-content>
            <div class="payment-confirmation-wallet__page-content">
                <div class="payment-confirmation-wallet__content">
                    <div class="payment-confirmation-wallet__label">{{ $t('payment.confirm-wallet.title') }}</div>
                    <div class="payment-confirmation-wallet__amount">{{ currencyFormatterAccount.format(amount * account.currency.rates.wallet) }}</div>
                    <f7-list dividers-ios strong-ios outline-ios class="payment-requisites">
                        <f7-list-item :header="$t('payment.confirm-wallet.write-off-amount')">
                            <template #title>
                                <span v-format-number="amount">{{ amount }} $</span>
                            </template>
                        </f7-list-item>
                        <f7-list-item :header="$t('payment.requisites.method')">
                            <template #title>
                                <div class="psystem-wrapper">
                                    <i class="icon bromoney-mini"></i>
                                    {{ $t('payment.requisites.flow-from-bromoney.address') }}
                                </div>
                            </template>
                        </f7-list-item>
                    </f7-list>
                </div>
                <f7-block class="payment-confirmation-wallet__actions">
                    <f7-button
                        class="theme-type"
                        preloader
                        @click="handleSubmit"
                        :disabled="isSubmitLoading"
                        :loading="isSubmitLoading"
                    >
                        {{ $t('payment.confirm-wallet.button') }}
                    </f7-button>
                </f7-block>
            </div>
        </f7-page-content>
    </f7-page>
</template>

<style scoped lang="scss">
.payment-confirmation-wallet {
  background-color: var(--white1);

  &__navbar {
    &:deep(.navbar-bg) {
      background-color: var(--white1);
    }
  }

  .page-content {
    padding-top: 0;
  }

  &__page-content {
    display: flex;
    flex-direction: column;
    height: inherit;
  }

  &__content {
    overflow-y: auto;
    flex: 1 1 auto;
  }

  &__label {
    text-align: center;
    font-size: 14px;
    font-weight: 500;
    line-height: 120%;
    color: #ACB4BE;
    padding-top: calc(var(--f7-page-navbar-offset, 0px) + var(--f7-page-toolbar-top-offset, 0px) + var(--f7-page-subnavbar-offset, 0px) + var(--f7-page-searchbar-offset, 0px) + var(--f7-page-content-extra-padding-top, 0px));
    margin-bottom: 0.5rem;
  }

  &__amount {
    text-align: center;
    font-size: 44px;
    font-weight: 700;
    line-height: 120%;
    margin-bottom: 1.5rem;
    color: var(--brown1);
  }

  &__actions {
    margin-top: 0;
    margin-bottom: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
  }
}
</style>
